import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Spinner from '../components/Spinner/spinner';
import { getRiskMetrics } from '../services/GuaranteesService';
import ProtectorSection from '../organisms/Protectors/ProtectorSection';
import { formatMoney } from '../utils/Masks';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { routes } from '../routes/routePaths';
export { formatMoney };

interface RiskMetricsData {
  integrated_fund?: number | null;
  pending_integration_fund?: number | null;
  authorized_risk_fund?: number | null;
  available_new_contributions?: number | null;
  active_contingent?: number | null;
  mora_total?: number | null;
}

export function getKpiGridColumns(windowWidth: number): string {
  if (windowWidth < 768) return '1fr';
  return 'repeat(3, 1fr)';
}

function useWindowWidth(): number {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const CONFIG_ERROR_MESSAGE =
  'Configuración del fondo no inicializada. Contactar a administración.';

const FundsAndProtectors = () => {
  const windowWidth = useWindowWidth();
  const gridColumns = getKpiGridColumns(windowWidth);
  const [metricsData, setMetricsData] = useState<RiskMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRiskMetrics();
  }, []);

  const fetchRiskMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getRiskMetrics();
      if (response.success && 'data' in response) {
        setMetricsData(response.data);
      } else if (response.status === 500) {
        const message =
          typeof response.message === 'string' && response.message.trim() !== ''
            ? response.message
            : CONFIG_ERROR_MESSAGE;
        toast.error(message);
        setMetricsData({});
      } else {
        setError('Error al cargar los datos de fondos y protectores');
        setMetricsData(null);
      }
    } catch (err) {
      setError('Error al cargar los datos de fondos y protectores');
      setMetricsData(null);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumb = (
    <PageBreadcrumb items={[{ label: 'Inicio', to: routes.dashboard }, { label: 'Fondos y protectores' }]} />
  );

  if (loading) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
        <Card width='85vw'>
          <Card display='flex' justifyContent='center' alignItems='center' height='60vh'>
            <Spinner />
          </Card>
        </Card>
      </>
    );
  }

  if (error || !metricsData) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
        <Card
          variant='neutral'
          modifier='inverted'
          spacing={'4'}
          height='-webkit-fill-available'
          width='85vw'
        >
          <Card display='flex' justifyContent='space-between'>
          <Card display='flex' alignItems='center'>
            <H withMargin={false} type='1' weight='bold' modifier='strong'>
              Fondos y protectores
            </H>
          </Card>
        </Card>
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            {error || 'No se pudieron cargar los datos'}
          </P>
          <P variant='primary' align='center'>
            Intente nuevamente más tarde.
          </P>
        </Card>
      </Card>
      </>
    );
  };

  const negativeStyle = { border: '2px solid #ef4444' };

  return (
    <>
      <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card
        variant='neutral'
        modifier='inverted'
        spacing={'4'}
        height='-webkit-fill-available'
        width='85vw'
      >
        <Card display='flex' justifyContent='space-between' alignItems='center'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Fondos y protectores
          </H>
        </Card>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '16px' }}>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: '16px',
          }}
        >
          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' flexDirection='column' gap='4px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatMoney(metricsData.integrated_fund)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Fondo integrado</H>
            </Card>
          </Card>

          <Card
            variant='neutral'
            modifier='regular'
            spacing={'4'}
            style={
              metricsData.pending_integration_fund != null && metricsData.pending_integration_fund < 0
                ? negativeStyle
                : undefined
            }
            data-testid='kpi-pending-integration'
          >
            <Card display='flex' flexDirection='column' gap='4px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatMoney(metricsData.pending_integration_fund)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Fondo pendiente integrar</H>
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' flexDirection='column' gap='4px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatMoney(metricsData.authorized_risk_fund)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Máximo autorizado</H>
            </Card>
          </Card>

          <Card
            variant='neutral'
            modifier='regular'
            spacing={'4'}
            style={
              metricsData.available_new_contributions != null && metricsData.available_new_contributions < 0
                ? negativeStyle
                : undefined
            }
            data-testid='kpi-available-contributions'
          >
            <Card display='flex' flexDirection='column' gap='4px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatMoney(metricsData.available_new_contributions)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Disponible aportes nuevos</H>
            </Card>
          </Card>

          <Card variant='neutral' modifier='regular' spacing={'4'}>
            <Card display='flex' flexDirection='column' gap='4px'>
              <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                {formatMoney(metricsData.active_contingent)}
              </H>
              <H withMargin={false} type='3' weight='bold' modifier='strong'>Contingente</H>
            </Card>
          </Card>

          <div data-testid='kpi-mora'>
            <Card variant='neutral' modifier='regular' spacing={'4'}>
              <Card display='flex' flexDirection='column' gap='4px'>
                <H withMargin={false} variant='neutral-1' type='1' weight='bold' modifier='strong'>
                  {formatMoney(metricsData.mora_total)}
                </H>
                <H withMargin={false} type='3' weight='bold' modifier='strong'>Mora</H>
              </Card>
            </Card>
          </div>
        </div>
      </div>
      <ProtectorSection />
      </Card>
    </>
  );
};

export default FundsAndProtectors;
